import { motion } from "framer-motion";
import { useCallback, useState } from "react";
import { LoaderFunction, redirect, useLoaderData, useNavigate } from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { useTranslation } from "@/core/i18n";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import {
  getReferralRewardDescription,
  getReferralRewardTitle,
  useLegacyReferAFriend,
  useShareReferralReward,
} from "@/modules/referralRewards";
import { storage } from "@/modules/storage";
import {
  getIncentivesByPlatformLocationIdActiveSignupReward,
  getIncentivesByPlatformLocationIdAvailableReferralReward,
  getPlatformCurrent,
  getUser,
} from "api/methods";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ShareGiftBox from "atoms/icon/icons/share-gift-box.svg";
import ShareV3 from "atoms/icon/icons/share-v3.svg";
import { TextButton } from "atoms/text-button/TextButton";
import { useClipboard } from "hooks";
import {
  formatPlatformNameForReferral,
  formatTitleForReferral,
} from "organisms/refer-a-friend/utils";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";
import { isNativeMobileApp } from "utils/platform.helpers";

export const V3ShareFriendsPage_URL = "/signin/share-friends";

type PlatformLoaderData = {
  platformSettings?: {
    rewardsEnabled?: boolean | null;
    patientPersonalisationEnabled?: boolean | null;
  } | null;
} | null;

const resolveNextPath = (platform: PlatformLoaderData) => {
  if (isNativeMobileApp()) {
    return "/signin/notifications";
  }
  if (platform?.platformSettings?.patientPersonalisationEnabled) {
    storage.setItem("personalisation_from_onboarding", "true");
    return "/personalise";
  }
  return "/home";
};

const load = async () => {
  const platform = await queryClient
    .fetchQuery({ ...getPlatformCurrent.keyed() })
    .catch(() => null);

  const user = await queryClient.fetchQuery({ ...getUser.keyed() }).catch(() => null);

  const referralRewardsEnabled = platform?.platformSettings?.rewardsEnabled;

  const platformLocationId = user?.platformLocation?.id;
  if (!platformLocationId) {
    throw redirect(resolveNextPath(platform));
  }

  const activeSignupReward = await queryClient
    .fetchQuery({
      ...getIncentivesByPlatformLocationIdActiveSignupReward.keyed({
        platformLocationId,
      }),
    })
    .catch(() => null);

  // If no signup reward exists, redirect
  if (!activeSignupReward) {
    throw redirect(resolveNextPath(platform));
  }

  let availableReferralReward = null;
  if (referralRewardsEnabled && activeSignupReward) {
    availableReferralReward = await queryClient
      .fetchQuery({
        ...getIncentivesByPlatformLocationIdAvailableReferralReward.keyed({
          platformLocationId,
        }),
      })
      .catch(() => null);
  }

  return waitObj({
    platform,
    user,
    activeSignupReward,
    availableReferralReward,
    referralRewardsEnabled,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const v3ShareFriendsLoader: LoaderFunction = () => load();

export default function V3ShareFriendsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const loaderData = useLoaderData() as LoaderData;
  const { platform, activeSignupReward, availableReferralReward, referralRewardsEnabled, user } =
    loaderData;

  const handleNext = useCallback(() => {
    if (isNativeMobileApp()) {
      navigate("/signin/notifications");
    } else if (platform?.platformSettings?.patientPersonalisationEnabled) {
      storage.setItem("personalisation_from_onboarding", "true");
      navigate("/personalise");
    } else {
      navigate("/home");
    }
  }, [navigate, platform]);

  // Setup for both referral flows
  const [referralUrl, setReferralUrl] = useState<string>("");
  const { copyToClipboard, isLoading: isCopyingLink } = useClipboard();

  const referrableRewardTitle = formatTitleForReferral(activeSignupReward?.title);
  const formattedPlatformName = formatPlatformNameForReferral(platform?.name);

  // Legacy referral hook (always called - React rules of hooks)
  const { shareLegacyReferral, handleCopyLink: handleLegacyCopyLink } = useLegacyReferAFriend({
    rewardsEnabled: !!referralRewardsEnabled,
    platform: platform!,
    title: referrableRewardTitle || "",
    formattedPlatformName: formattedPlatformName || "",
    copyToClipboard: (text: string) => {
      copyToClipboard(text);
    },
    isCopyingLink,
    referralUrl,
    setReferralUrl,
  });

  // New referral rewards hook (always called - React rules of hooks)
  const { shareReferralReward } = useShareReferralReward({
    platform: platform!,
    user: user,
    rewardTitle: activeSignupReward?.title || "",
    shareCopyTemplate: availableReferralReward?.platformLocationReferralRewardCopyTemplate,
  });
  // If availableReferralReward exists, use the new referral rewards flow
  // Otherwise, use the legacy referral flow
  const handleReferFriendClick = async () => {
    if (availableReferralReward) {
      const generatedUrl = await shareReferralReward();

      if (generatedUrl) {
        setReferralUrl(generatedUrl);
      }
      return;
    }

    shareLegacyReferral();
  };

  // Handle copy link
  const handleCopyLink = async () => {
    if (referralUrl) {
      await copyToClipboard(referralUrl);
      return;
    }

    if (!availableReferralReward) {
      handleLegacyCopyLink();
    }
  };

  const shareButtonLabel = availableReferralReward
    ? t("v3ShareFriendsPage.referAFriend")
    : t("v3ShareFriendsPage.inviteFriends");

  const renderCopyLinkButton = () => {
    if (!referralUrl) {
      return null;
    }

    return (
      <div className="mt-1 w-full">
        <Button style="secondary" fullWidth onClick={handleCopyLink} disabled={isCopyingLink}>
          {t("v3ShareFriendsPage.copyLink")}
        </Button>
      </div>
    );
  };

  return (
    <MobileViewportContainer header={<OnboardingBackButton />}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        className="flex w-full max-w-sm flex-col items-center gap-9"
      >
        {/* Share Icon - 36px gap after */}
        <Icon svg={ShareV3} size="large" color="brandColor" />
        {/* Title - 36px gap after */}
        <p className="w-full shrink-0 text-center font-serif text-[30px] font-normal italic leading-[32px] text-[#091f24]">
          {referralRewardsEnabled && availableReferralReward
            ? t("v3ShareFriendsPage.titleWithReward")
            : t("v3ShareFriendsPage.titleWithoutReward")}
        </p>
        {/* Card with message */}
        <div className="relative w-full shrink-0 overflow-hidden rounded-[15px] bg-white shadow-[0px_2px_10px_0px_rgba(0,0,0,0.06)]">
          {/* Background gift box decoration - only show when referral rewards available */}
          {referralRewardsEnabled && availableReferralReward && (
            <div className="absolute -bottom-5 -right-12 h-[195px] w-[192px]">
              <Icon
                svg={ShareGiftBox}
                size="x3large"
                color="brandColor"
                className="h-full w-full"
              />
            </div>
          )}

          {/* Card content */}
          {referralRewardsEnabled && availableReferralReward ? (
            <div className="relative flex flex-col gap-[18px] px-[34px] py-8">
              <div className="flex flex-col gap-4">
                <p className="font-serif text-[24px] font-normal italic leading-[32px] text-[#091f24]">
                  {getReferralRewardTitle(availableReferralReward, activeSignupReward)}
                </p>
                <p
                  className="font-sans text-sm font-normal leading-[18px] text-[#6b7070]"
                  style={{ fontVariationSettings: "'opsz' 14" }}
                >
                  {getReferralRewardDescription(
                    availableReferralReward,
                    activeSignupReward?.title ?? "",
                  )}
                </p>
              </div>
            </div>
          ) : (
            <div className="relative flex flex-col gap-[18px] px-[34px] py-16">
              <p className="text-center font-serif text-[24px] font-normal italic leading-[32px] text-[#091f24]">
                {t("v3ShareFriendsPage.sendRewardMessage", {
                  rewardTitle: activeSignupReward?.title,
                })}
              </p>
            </div>
          )}
        </div>
        {/* Buttons - 16px gap between them */}
        <div className="flex w-full shrink-0 flex-col items-center gap-4">
          <Button fullWidth onClick={handleReferFriendClick}>
            {shareButtonLabel}
          </Button>
          {renderCopyLinkButton()}
          <TextButton className="px-3 font-normal" onClick={handleNext}>
            {t("v3ShareFriendsPage.later")}
          </TextButton>
        </div>
      </motion.div>
    </MobileViewportContainer>
  );
}
